import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Share2, Download, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { DAILY_UI as UI } from "@/data/dailyReminders";
import { REMINDER_UI } from "@/data/manasikReminders";
import { makeBadgeCard, shareBadgeCard, saveBadgeCard } from "@/lib/streakBadgeCard";
import { fill } from "@/lib/utils";

// Global celebration popup: listens for "worship-badge" events (fired by worshipLog.markDone when a
// 7/30-day streak is first reached) and shows the shareable medal card.
export function BadgeCelebration() {
  const { tc, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(null);
  const cardRef = useRef(null);
  const tier = queue[0];

  useEffect(() => {
    const onBadge = (e) => setQueue((q) => (q.includes(e.detail.tier) ? q : [...q, e.detail.tier]));
    window.addEventListener("worship-badge", onBadge);
    return () => window.removeEventListener("worship-badge", onBadge);
  }, []);

  useEffect(() => {
    if (!tier) { setCard(null); return; }
    let cancelled = false;
    makeBadgeCard({ tier, name: user?.name, tc, lang }).then((c) => {
      if (cancelled) { URL.revokeObjectURL(c.url); return; }
      cardRef.current = c; setCard(c);
    }).catch(() => setCard(null));
    return () => { cancelled = true; if (cardRef.current) { URL.revokeObjectURL(cardRef.current.url); cardRef.current = null; } };
  }, [tier, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => setQueue((q) => q.slice(1));
  const share = async () => {
    if (!cardRef.current) return;
    setBusy("share");
    try { await shareBadgeCard(cardRef.current, tier); toast.success(tc(UI.cardReady)); }
    catch { toast.error(tc(REMINDER_UI.shareFail)); }
    finally { setBusy(null); }
  };
  const save = () => { if (cardRef.current) { saveBadgeCard(cardRef.current, tier); toast.success(tc(UI.cardReady)); } };

  return (
    <Dialog open={!!tier} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="badge-dialog" data-tier={tier}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl" data-testid="badge-dialog-title">
            <Award className="h-6 w-6 text-gold" /> {fill(tc(UI.badgeUnlocked), { n: tier })}
          </DialogTitle>
          <DialogDescription data-testid="badge-dialog-desc">{fill(tc(UI.badgeCongrats), { n: tier })}</DialogDescription>
        </DialogHeader>
        {card ? (
          <img src={card.url} alt={fill(tc(UI.badgeName), { n: tier })} className="w-full rounded-2xl shadow-lg ring-1 ring-[hsl(var(--gold))]/40" data-testid="badge-preview" />
        ) : (
          <div className="flex aspect-square w-full items-center justify-center rounded-2xl bg-secondary" data-testid="badge-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        )}
        <DialogFooter className="flex-wrap gap-2 sm:justify-center">
          <Button onClick={share} disabled={!card || !!busy} data-testid="badge-share" className="h-11 rounded-full px-5">
            {busy === "share" ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Share2 className="me-2 h-4 w-4" />} {tc(UI.shareBtn)}
          </Button>
          <Button variant="outline" onClick={save} disabled={!card} data-testid="badge-save" className="h-11 rounded-full px-5">
            <Download className="me-2 h-4 w-4" /> {tc(UI.saveBtn)}
          </Button>
          <Button variant="ghost" onClick={() => { close(); navigate("/profile"); }} data-testid="badge-view" className="h-11 rounded-full px-5">{tc(UI.badgeView)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
